import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { useWebRTC } from './useWebRTC';

const CallContext = createContext(null);

export function CallProvider({ children }) {
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [callStartAt, setCallStartAt] = useState(null); // epoch ms when call started/joined

  // Keep useWebRTC mounted globally; it will be inert if activeRoomId is null
  const { state, actions } = useWebRTC({ roomId: activeRoomId, displayName: displayName || 'Me', groupName: groupName || '' });

  // Important: do NOT auto-clear activeRoomId based only on joined=false, because
  // there is a short window between starting a call and join() completing.
  // Clearing here would cancel the call before it starts.

  const startCall = useCallback(async (roomId, name, group) => {
    if (!roomId) return false;
    setDisplayName(name || 'Me');
    setGroupName(group || '');
    setActiveRoomId(roomId);
    // Consumer (CallPage) should call join() after this.
    return true;
  }, []);

  const endAndClear = useCallback(() => {
    try { actions.endCall(); } catch (e) {}
    // Ensure local media is stopped and cleaned up as well
    try { actions.leave(); } catch (e) {}
    // clear timer persistence
    try {
      if (activeRoomId) localStorage.removeItem(`call.start.${activeRoomId}`);
    } catch (_) {}
    setCallStartAt(null);
    setActiveRoomId(null);
    setGroupName('');
  }, [actions, activeRoomId]);

  const leaveAndClear = useCallback(() => {
    try { actions.leave(); } catch (e) {}
    try {
      if (activeRoomId) localStorage.removeItem(`call.start.${activeRoomId}`);
    } catch (_) {}
    setCallStartAt(null);
    setActiveRoomId(null);
    setGroupName('');
  }, [actions, activeRoomId]);

  // Load persisted call start when roomId becomes active
  useEffect(() => {
    if (!activeRoomId) { setCallStartAt(null); return; }
    try {
      const v = localStorage.getItem(`call.start.${activeRoomId}`);
      if (v) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) setCallStartAt(n);
      }
    } catch (_) {}
  }, [activeRoomId]);

  // When actually joined, set/start callStartAt if not already set, and persist
  useEffect(() => {
    if (!activeRoomId) return;
    if (state?.joined) {
      setCallStartAt(prev => {
        const ts = prev && Number.isFinite(prev) ? prev : Date.now();
        try { localStorage.setItem(`call.start.${activeRoomId}`, String(ts)); } catch (_) {}
        return ts;
      });
    }
  }, [state?.joined, activeRoomId]);

  // Persist session info when actually joined; clear when no activeRoomId
  useEffect(() => {
    try {
      const key = 'call.session';
      if (activeRoomId && state?.joined) {
        const payload = {
          roomId: activeRoomId,
          displayName: displayName || 'Me',
          groupName: groupName || '',
          ts: Date.now(),
        };
        sessionStorage.setItem(key, JSON.stringify(payload));
      } else if (!activeRoomId) {
        sessionStorage.removeItem(key);
      }
    } catch (_) {}
  }, [activeRoomId, displayName, groupName, state?.joined]);

  // Auto clear active room if host ended the meeting
  useEffect(() => {
    if (state?.endedByHost) {
      try { if (activeRoomId) localStorage.removeItem(`call.start.${activeRoomId}`); } catch (_) {}
      setCallStartAt(null);
      setActiveRoomId(null);
      setGroupName('');
    }
  }, [state?.endedByHost, activeRoomId]);

  const value = useMemo(() => ({
    activeRoomId,
    displayName,
    groupName,
    callStartAt,
    state,
    actions: {
      ...actions,
      startCall,
      endAndClear,
      leaveAndClear,
      setDisplayName,
    }
  }), [activeRoomId, displayName, groupName, callStartAt, state, actions, startCall, endAndClear, leaveAndClear]);

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
}

export function useCall() {
  return useContext(CallContext);
}
